import React, { useCallback, useMemo, useRef, useState } from "react";
import { TableHeaderColumn } from "react-bootstrap-table";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import _ from "lodash";
import { dateTimeReportFormatter } from "../../fomatters/DateFormatter";
import { useDataFormatterHook } from "../../../hooks/useDataFormatterHook";
import InputSelect from "../InputSelect";
import ReportByGame from "../ReportByGame";

const startDateValue = new Date().toISOString().split("T")[0];
const endDateValue = new Date().toISOString().split("T")[0];

// El rotation del local no viene como campo; está embebido al inicio del nombre.
const extractRotation = (name) => {
  if (!name) return "";
  const match = String(name).trim().match(/^\d+/);
  return match ? match[0] : "";
};

const SORT_OPTIONS = [
  { value: "visitorNum", label: "Rotation" },
  { value: "gameDateTime", label: "Game Date" },
];

const sortKey = (group, field) => {
  const v = group?.Values?.[0] || {};
  switch (field) {
    case "gameDateTime":
      return new Date(v.gameDateTime).getTime() || 0;
    case "visitorNum":
    default:
      return Number(v.visitorNum) || 0;
  }
};

/**
 * TABLE FREEPLAYOFFICEOPTIONS
 */
function GameScore() {
  const reportRef = useRef();
  const [startDate, setStartDate] = useState(startDateValue);
  const onStartDateChange = useCallback(
    (event) => setStartDate(event.target.value),
    []
  );

  const [endDate, setEndDate] = useState(endDateValue);
  const onEndDateChange = useCallback(
    (event) => setEndDate(event.target.value),
    []
  );

  const [sport, setSport] = useState("");
  const onSportChanged = useCallback((s) => setSport(s), []);
  const { values: sports } = useDataFormatterHook({
    addEmpty: true,
    value: "idSport",
    text: "sportName",
    path: `/Player/GetSports`,
  });

  // Búsqueda por rotation + orden, aplicados sobre los datos ya cargados.
  const [searchRotation, setSearchRotation] = useState("");
  const [sortField, setSortField] = useState("visitorNum");
  const [sortDir, setSortDir] = useState("asc");

  const onSearchRotationChange = useCallback(
    (event) => setSearchRotation(event.target.value),
    []
  );
  const onSortFieldChange = useCallback(
    (event) => setSortField(event.target.value),
    []
  );
  const onSortDirChange = useCallback(
    (event) => setSortDir(event.target.value),
    []
  );

  const viewTransform = useCallback(
    (groups) => {
      if (!Array.isArray(groups)) return groups;
      let result = groups;

      const query = searchRotation.trim().toLowerCase();
      if (query) {
        result = result.filter((group) => {
          const v = group?.Values?.[0] || {};
          return (
            String(v.visitorNum ?? "").toLowerCase().includes(query) ||
            extractRotation(v.homeName).toLowerCase().includes(query) ||
            String(v.visitorName ?? "").toLowerCase().includes(query) ||
            String(v.homeName ?? "").toLowerCase().includes(query)
          );
        });
      }

      return _.orderBy(result, [(group) => sortKey(group, sortField)], [sortDir]);
    },
    [searchRotation, sortField, sortDir]
  );

  const toolbar = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by rotation or name"
          value={searchRotation}
          onChange={onSearchRotationChange}
          style={{ width: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#999" }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" style={{ width: 130 }}>
          <InputLabel shrink>Order by</InputLabel>
          <Select
            value={sortField}
            onChange={onSortFieldChange}
            label="Order by"
          >
            {SORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ width: 110 }}>
          <InputLabel shrink>Direction</InputLabel>
          <Select
            value={sortDir}
            onChange={onSortDirChange}
            label="Direction"
          >
            <MenuItem value="asc">ASC</MenuItem>
            <MenuItem value="desc">DESC</MenuItem>
          </Select>
        </FormControl>
      </div>
    ),
    [
      searchRotation,
      sortField,
      sortDir,
      onSearchRotationChange,
      onSortFieldChange,
      onSortDirChange,
    ]
  );

  return (
    <div className="flex flex-col gap-4 p-4 h-max">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          required
          variant="outlined"
          label="From"
          type="date"
          value={startDate}
          size="small"
          onChange={onStartDateChange}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          required
          variant="outlined"
          label="To"
          type="date"
          value={endDate}
          size="small"
          onChange={onEndDateChange}
          InputLabelProps={{ shrink: true }}
        />
        <InputSelect
          variant="outlined"
          size="small"
          label="Sport"
          name="name"
          data={sports}
          placeholder="DataFormatter"
          setSelectValue={onSportChanged}
          startValue=""
        />
      </div>

      <ReportByGame
        ref={reportRef}
        title="Game Score Report"
        toolbar={toolbar}
        viewTransform={viewTransform}
        getUrl={() => "/Reports/Report_Game_Score_2"}
        getParams={({ userId }) => ({
          IdUser: Number(userId),
          StartDate: startDate,
          EndDate: endDate,
          Sport: sport,
        })}
        getProcess={(sport, values) => {
          switch (sport) {
            case "tnt":
              return [values?.[0], ...values].map(
                (
                  {
                    Id,
                    idSport,
                    gameDateTime,
                    homeName,
                    visitorName,
                    gradedDate,
                    gameStat,
                    result,
                  },
                  index
                ) => {
                  const finished = gameStat === "F";
                  if (index === 0) {
                    return {
                      Id: `${Id}V`,
                      sport: idSport,
                      date: gameDateTime,
                      team: visitorName,
                      gradedDate,
                    };
                  } else {
                    const team = `${homeName}${result ? " [Winner]" : ""}`;
                    return {
                      Id: `${Id}H`,
                      sport: "",
                      date: "",
                      team: team,
                      gradedDate: "",
                    };
                  }
                }
              );
            default:
              const {
                Id,
                idSport,
                gameDateTime,
                homeName,
                visitorName,
                gradedDate,
                gameStat,
                gVisitorScore,
                gHomeScore,
                visitorScore1H,
                homeScore1H,
                visitorScore2H,
                homeScore2H,
                visitorScore1Q,
                homeScore1Q,
                visitorScore2Q,
                homeScore2Q,
                visitorScore3Q,
                homeScore3Q,
                visitorScore4Q,
                homeScore4Q,
              } = values?.[0];
              const finished = gameStat === "F";
              return [
                {
                  Id: `${Id}V`,
                  sport: idSport,
                  date: gameDateTime,
                  team: visitorName,
                  game: finished ? gVisitorScore : "Cancelled",
                  score1H: visitorScore1H,
                  score2H: visitorScore2H,
                  score1Q: visitorScore1Q,
                  score2Q: visitorScore2Q,
                  score3Q: visitorScore3Q,
                  score4Q: visitorScore4Q,
                  gradedDate,
                },
                {
                  Id: `${Id}H`,
                  sport: "",
                  date: "",
                  team: homeName,
                  game: finished ? gHomeScore : "",
                  score1H: homeScore1H,
                  score2H: homeScore2H,
                  score1Q: homeScore1Q,
                  score2Q: homeScore2Q,
                  score3Q: homeScore3Q,
                  score4Q: homeScore4Q,
                  gradedDate: "",
                },
              ];
          }
        }}
      >
        <TableHeaderColumn dataField="Id" isKey hidden>
          Id
        </TableHeaderColumn>
        <TableHeaderColumn dataField="sport">
          Sport
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="date"
          dataFormat={dateTimeReportFormatter}
        >
          Game Date
        </TableHeaderColumn>
        <TableHeaderColumn dataField="team">
          Visitor / Home
        </TableHeaderColumn>
        <TableHeaderColumn dataField="game">
          Game
        </TableHeaderColumn>
        <TableHeaderColumn dataField="score1H">
          1H
        </TableHeaderColumn>
        <TableHeaderColumn dataField="score2H">
          2H
        </TableHeaderColumn>
        <TableHeaderColumn dataField="score1Q">
          1Q
        </TableHeaderColumn>
        <TableHeaderColumn dataField="score2Q">
          2Q
        </TableHeaderColumn>
        <TableHeaderColumn dataField="score3Q">
          3Q
        </TableHeaderColumn>
        <TableHeaderColumn dataField="score4Q">
          4Q
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="gradedDate"
          dataFormat={dateTimeReportFormatter}
        >
          Graded
        </TableHeaderColumn>
      </ReportByGame>
    </div>
  );
}

export default GameScore;
